import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import apiConfig from '../../config/apiConfig.js';

interface PaymentHistoryItem {
  id: string;
  date: string;
  type: string;
  amount: string;
  method: string;
  status: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalTransactions: number;
  limit: number;
}

export const usePaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalTransactions: 0,
    limit: 10,
  });

  const fetchTransactionHistory = async (page: number = 1) => {
    setPaymentHistoryLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/transaction-history?page=${page}&limit=10`,
        {
          headers: {
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      // Process the API response to match our PaymentHistory component interface
      if (
        response.data &&
        response.data.data &&
        response.data.data.transactions
      ) {
        const transactions = response.data.data.transactions.map(
          (transaction: any): PaymentHistoryItem => ({
            id: transaction.transactionId || transaction.id || transaction._id,
            date: transaction.date
              ? new Date(transaction.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
              : "N/A",
            type:
              transaction.type === "gift"
                ? "Gift Purchase"
                : transaction.type === "subscription"
                ? "Subscription"
                : transaction.type || "Payment",
            amount: transaction.amountPaid
              ? `$${transaction.amountPaid.toFixed(2)}`
              : "N/A",
            method:
              transaction.paymentMethod ||
              (transaction.cardDetails
                ? `**** **** **** ${transaction.cardDetails.last4}`
                : "Card"),
            status: transaction.status || "Success",
          })
        );

        setPaymentHistory(transactions);

        // Update pagination info
        if (response.data.data.pagination) {
          setPagination({
            currentPage: response.data.data.pagination.currentPage || page,
            totalPages: response.data.data.pagination.totalPages || 1,
            totalTransactions: response.data.data.pagination.totalTransactions || transactions.length,
            limit: response.data.data.pagination.limit || 10,
          });
        }
      } else {
        setPaymentHistory([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalTransactions: 0,
          limit: 10,
        });
      }
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      if (axios.isAxiosError(error)) {
        console.error("Request details:", {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
      toast.error("Failed to load payment history");
      setPaymentHistory([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalTransactions: 0,
        limit: 10,
      });
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  return {
    paymentHistory,
    paymentHistoryLoading,
    pagination,
    fetchTransactionHistory
  };
};